<template lang="pug">
#outing-edit(v-cloak)
  .box(v-if="outing")
    .box-header.with-border.register-header
      .title
        span {{ outing.targetResidentInfo.name }}（{{ outing.targetResidentInfo.roomName}}）様の届出編集

    .box-body.register-body
      //- 届出種類
      FormLabel(key-name="届出種類" :value="_outingTypeName")
      //- 外出日時
      .datetime(v-if="outing.type == 'goingOut'")
        InputGoingOutDatetime(key-name="外出日時"
          :showYear="true"
          :year.sync="startYear" :years="_startYears"
          :month.sync="startMonth" :months="startMonths"
          :day.sync="startDay" :days="startDays"
          :startHour.sync="startHour" :startHours="startHours"
          :startMin.sync="startMin" :startMins="startMins"
          :endHour.sync="endHour" :endHours="endHours"
          :endMin.sync="endMin" :endMins="endMins"
          :realtimeError="outingDateErrorMessage1"
          validationDateKeyName="外出日時" required)
      .datetime(v-if="outing.type == 'stayingOut'")
        InputStayingOutDatetime(key-name1="外泊開始日時" key-name2="外泊終了日時"
          :showYear="true"
          :year.sync="startYear" :years="_startYears"
          :month.sync="startMonth" :months="startMonths"
          :day.sync="startDay" :days="startDays"
          :startHour.sync="startHour" :startHours="startHours"
          :startMin.sync="startMin" :startMins="startMins"
          :stayCount.sync="stayingCount" :stayCounts="stayingCounts"
          :endHour.sync="endHour" :endHours="endHours"
          :endMin.sync="endMin" :endMins="endMins"
          :realtimeError1="outingDateErrorMessage1"
          :realtimeError2="outingDateErrorMessage2"
          validationDateKeyName="外出日時" required)
      //- 主な外出場所
      FormInput(v-model="outing.location" name="location" key-name="主な外出場所" placeholder="主な外出場所"
        :maxlength="100" type="text" :validation-rules="{ required: true, max: 100 }" required)
      //- 申請者
      FormLabel(key-name="申請者" :value="_applicantFamily")
      //- お迎えに来る方
      FormInputRadio(v-model="outing.applicantPickup" name="applicant-pickup" key-name="お迎えに来る方"
        :options="_applicantPickupOptions" :validation-rules="{ required: true }" required)
      //- お迎えに来る方（自由記述）
      .pickup-person(v-if="!outing.applicantPickup")
        //- お迎えに来る方（氏名）
        FormInput(v-model="outing.pickupPerson.name" name="pickup-person-name" key-name="お迎えに来る方（氏名）"
          type="text" placeholder="お迎えに来る方（氏名）" :maxlength="32" :validation-rules="{ required: true, max: 32 }"
          :styles="{ width: '340px' }" required)
        //- お迎えに来る方（続柄）
        FormInput(v-model="outing.pickupPerson.relation" name="pickup-person-relation" key-name="お迎えに来る方（続柄）"
          type="text" placeholder="お迎えに来る方（続柄）" :maxlength="10" :validation-rules="{ required: true, max: 10 }"
          :styles="{ width: '340px' }" required)
      //- 外出手段
      FormInput(v-if="_showSelectedVehicle" v-model="outing.selectedVehicle" name="selected-vehicle" key-name="外出手段" placeholder="外出手段"
        :maxlength="100" type="text" :validation-rules="{ required: true, max: 100 }" required)
      //- 食事等
      InputOutingNeedsMeal(v-if="_showOutingNeedsMeal" v-model="_inputOutingNeedsMealModel" :setting="setting")
      //- 家族からの連絡事項
      FormTextarea(v-model="outing.text" name="text" key-name="家族からの連絡事項"
        placeholder="例：家族からの連絡事項をご記載ください。"
        :validation-rules="{ max: 1000 }" :maxlength="1000" :styles="{ maxWidth: '640px', width: '640px', minHeight: '160px' }")

    //- エラーメッセージ
    .register-row(v-show="errorMessage")
      .register-group
        .register-value
          .error-message {{ errorMessage }}

    .box-footer.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons.d-flex.flex-row.justify-content-end
        button.btn.edit-btn(type="button" @click="onUpdate") 変更
        button.btn.cancel-btn(type="button" @click="gotoOutingDetail") キャンセル

  //- 更新モーダル
  OkCancelModal(v-model="showUpdateConfirmModal" :title="updateConfirmModalTitle" @ok="updateOuting(outing)")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoOutingDetail")

  //- 画面全体ローディング
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import OutingsService, { NeedsMeal, OutingAvailableDatesGetModel, OutingGetModel, OutingPatchModel, OutingType } from "@/commons/services/Outings";
import OutingSettingsService, { OutingSettingGetModel } from "@/commons/services/OutingSettings";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import InputGoingOutDatetime from "@/pc/components/outings/InputGoingOutDatetime.vue";
import InputStayingOutDatetime from "@/pc/components/outings/InputStayingOutDatetime.vue";
import InputOutingNeedsMeal, { InputOutingNeedsMealModel } from "@/pc/components/outings/InputOutingNeedsMeal.vue";

@Component({
  components: {
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal,
    FormInputRadio,
    FormDatetimePicker,
    FormInput,
    FormLabel,
    FormTextarea,
    InputGoingOutDatetime,
    InputStayingOutDatetime,
    InputOutingNeedsMeal
  },
  $_veeValidate: { validator: "new" }
})
export default class OutingEdit extends Vue {
  /** 外出外泊サービス */
  @Inject("OutingsService") outingsService!: OutingsService;
  /** 外出外泊設定サービス */
  @Inject("OutingSettingsService") outingSettingsService!: OutingSettingsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 初期化が終わったかどうか（Watchの判定で使用する） */
  isInitialized: boolean = false;

  /** エラーメッセージ */
  errorMessage: string = "";
  /** 外出外泊届けエラーメッセージ */
  outingDateErrorMessage1: string = "";
  /** 外出外泊届けエラーメッセージ */
  outingDateErrorMessage2: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 外出外泊届け設定 */
  setting: OutingSettingGetModel | null = null;
  /** 外出外泊届け */
  outing: OutingGetModel | null = null;
  /** 外出外泊可能日時（開始） */
  outingAvailableStartDates: OutingAvailableDatesGetModel | null = null;
  /** 外出外泊可能日時（終了） */
  outingAvailableEndDates: OutingAvailableDatesGetModel | null = null;

  /** 開始年 */
  startYear: string = "";
  /** 開始月 */
  startMonth: string = "";
  /** 開始月選択項目 */
  startMonths: Array<string> = [];
  /** 開始日 */
  startDay: string = "";
  /** 開始日選択項目 */
  startDays: Array<string> = [];
  /** 開始時 */
  startHour: string = "";
  /** 開始時選択項目 */
  startHours: Array<string> = [];
  /** 開始分 */
  startMin: string = "";
  /** 開始分選択項目 */
  startMins: Array<string> = [];
  /** 宿泊日数 */
  stayingCount: string = "";
  /** 宿泊日数の単位 */
  stayingCounts: Array<{ label: string; value: string }> = [];
  /** 終了時 */
  endHour: string = "";
  /** 終了時選択項目 */
  endHours: Array<string> = [];
  /** 終了分 */
  endMin: string = "";
  /** 終了分選択項目 */
  endMins: Array<string> = [];

  /** 外出外泊開始日の食事が必要かどうか */
  startDateNeedsMeal: NeedsMeal = {
    breakfast: true,
    lunch: true,
    snack: true,
    dinner: true
  };
  /** 外出外泊終了日の食事が必要かどうか */
  endDateNeedsMeal: NeedsMeal | null | undefined = {
    breakfast: true,
    lunch: true,
    snack: true,
    dinner: true
  };

  /** 更新確認モーダル表示可否 */
  showUpdateConfirmModal: boolean = false;
  /** 更新確認モーダルタイトル */
  updateConfirmModalTitle: string = "";
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** ### 届出種類選択肢 */
  get _outingTypeName(): string {
    if (!this.outing) {
      return "";
    }
    return this.outing?.type == OutingType.GOING_OUT ? "外出届け" : this.outing?.type == OutingType.STAYING_OUT ? "外泊届け" : "";
  }
  /** ### 開始年の選択肢 */
  get _startYears(): Array<{ label: string; value: number }> {
    const years: Array<{ label: string; value: number }> = [];
    for (let i = 0; i < 2; i++) {
      const date: Date = new Date();
      date.setFullYear(date.getFullYear() + i);
      const intlOptions: Intl.DateTimeFormatOptions = { year: "numeric" };
      const jpYear: string = new Intl.DateTimeFormat("ja-JP-u-ca-japanese", intlOptions).format(date).slice(0, -1); // 年が重複するので最終文字だけ削除;
      years.push({ label: `${date.getFullYear()}（${jpYear}）`, value: date.getFullYear() });
    }
    return years;
  }
  /** ### 申請者 */
  get _applicantFamily(): string {
    if (!this.outing) {
      return "";
    }
    return `${this.outing.applicantFamilyInfo.name}（${this.outing.applicantFamilyInfo.relation}）`;
  }
  /** ### お迎えに来る方の選択肢 */
  get _applicantPickupOptions(): Array<{ value: boolean; label: string }> {
    return [
      { value: true, label: "申請者と同じ" },
      { value: false, label: "申請者以外" }
    ];
  }
  /** 外出手段 */
  get _showSelectedVehicle(): boolean {
    if (!this.outing || !this.setting) {
      return false;
    } else {
      return this.setting.useSelectedVehicle;
    }
  }
  /** ### 食事キャンセル情報入力を表示するか否か */
  get _showOutingNeedsMeal(): boolean {
    if (!this.outing || !this._inputedStartAndEndDatetime) {
      return false;
    } else {
      return true;
    }
  }
  get _inputedStartAndEndDatetime(): boolean {
    // 共通部分が入力されているかを判定
    const inputedDatetime =
      this.startYear != "" && this.startMonth != "" && this.startDay != "" && this.startHour != "" && this.startMin != "" && this.endHour != "" && this.endMin != "" ? true : false;
    // 外出の場合はそのまま返す
    if (this.outing && this.outing.type == OutingType.GOING_OUT) {
      return inputedDatetime;
    }
    // 外泊の場合は宿泊日数が入力さているかも合わせて返す
    else if (this.outing && this.outing.type == OutingType.STAYING_OUT) {
      return inputedDatetime && this.stayingCount != "";
    }
    return false;
  }
  /** ### 食事キャンセル情報 */
  get _inputOutingNeedsMealModel(): InputOutingNeedsMealModel | null {
    if (!this.outing) {
      return null;
    }

    // 開始日時に関連するものが1つでも未入力であればnullを返す
    if (isNaN(Number(this.startYear)) || isNaN(Number(this.startMonth)) || isNaN(Number(this.startDay)) || isNaN(Number(this.startHour)) || isNaN(Number(this.startMin))) {
      return null;
    }
    // 終了日時に関連するものが1つでも未入力であればnullを返す
    if (isNaN(Number(this.endHour)) || isNaN(Number(this.endMin))) {
      return null;
    }

    // 開始日時を作成する
    const startDate = new Date(Number(this.startYear), Number(this.startMonth) - 1, Number(this.startDay), Number(this.startHour), Number(this.startMin));
    const startDatetime = this.$dateUtil.convertDateToNumber(startDate, "yyyymmddhhMM00000");
    // 終了日時を作成する
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + Number(this.stayingCount)); // 開始日に宿泊数を加算
    endDate.setHours(Number(this.endHour)); // 終了時を設定
    endDate.setMinutes(Number(this.endMin)); // 終了分を設定
    const endDatetime = this.$dateUtil.convertDateToNumber(endDate, "yyyymmddhhMM00000");
    return {
      /** 外出外泊種別 */
      type: this.outing.type as OutingType,
      /** 開始日時 */
      startDatetime,
      /** 開始日時の食事情報 */
      startDateNeedsMeal: this.startDateNeedsMeal,
      /** 終了日時 */
      endDatetime,
      /** 終了日時の食事情報 */
      endDateNeedsMeal: this.endDateNeedsMeal as NeedsMeal
    };
  }

  /**
   * ### 開始年の値更新を監視する
   *
   * - 月以降の入力値をリセットする
   * - エラーメッセージをリセットする
   * - 開始月のセレクトボックスの選択肢を更新する
   */
  @Watch("startYear")
  public changeStartYear(newValue: string): void {
    // 更新対象の外出外泊届け、外出外泊設定が未取得の場合は処理をスキップする
    // 初期化処理が終わるまでは処理をスキップする
    if (!this.outing || !this.setting || !this.isInitialized) {
      return;
    }

    this.startMonth = "";
    this.startDay = "";
    this.startHour = "";
    this.startMin = "";
    this.stayingCount = "";
    this.endHour = "";
    this.endMin = "";

    this.outingDateErrorMessage1 = "";
    this.outingDateErrorMessage2 = "";

    const startMonths: Array<string> = this.createStartMonths(Number(newValue));
    this.startMonths = startMonths;

    if (startMonths.length == 0) {
      this.outingDateErrorMessage1 = "選択した年は外出外泊届けが出来ません。他の年を選択してください。";
    }

    return;
  }

  /**
   * ### 開始月の値更新を監視する
   *
   * - 開始日以降の入力値をリセットする
   * - エラーメッセージをリセットする
   * - 値がリセットされた場合は処理をしない
   * - 開始日のセレクトボックスの選択肢を更新する
   */
  @Watch("startMonth")
  public async changeStartMonth(newValue: string): Promise<void> {
    // 更新対象の外出外泊届け、外出外泊設定が未取得の場合は処理をスキップする
    // 初期化処理が終わるまでは処理をスキップする
    if (!this.outing || !this.setting || !this.isInitialized) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      this.startDay = "";
      this.startHour = "";
      this.startMin = "";
      this.stayingCount = "";
      this.endHour = "";
      this.endMin = "";

      this.outingDateErrorMessage1 = "";
      this.outingDateErrorMessage2 = "";

      if (!newValue) {
        this.isBusy = false;
        return;
      }

      const startDays: Array<string> = await this.createStartDays(Number(this.startYear), Number(newValue));
      this.startDays = startDays;

      if (startDays.length == 0) {
        this.outingDateErrorMessage1 = "選択した月は外出外泊届けが出来ません。他の月を選択してください。";
      }
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }
    return;
  }

  /**
   * ### 開始日の値更新を監視する
   *
   * - 開始時以降の入力値をリセットする
   * - エラーメッセージをリセットする
   * - 値がリセットされた場合は処理をしない
   * - 開始時のセレクトボックスの選択肢を更新する
   */
  @Watch("startDay")
  public changeStartDay(newValue: string): void {
    // 更新対象の外出外泊届け、外出外泊設定が未取得の場合は処理をスキップする
    // 初期化処理が終わるまでは処理をスキップする
    if (!this.outing || !this.setting || !this.isInitialized) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      this.startHour = "";
      this.startMin = "";
      this.stayingCount = "";
      this.endHour = "";
      this.endMin = "";

      this.outingDateErrorMessage1 = "";
      this.outingDateErrorMessage2 = "";

      if (!newValue) {
        this.isBusy = false;
        return;
      }

      const startHours: Array<string> = this.createStartHours(Number(newValue));
      this.startHours = startHours;

      if (startHours.length == 0) {
        this.outingDateErrorMessage1 = "選択した日は外出外泊届けが出来ません。他の日を選択してください。";
      }
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }
    return;
  }

  /**
   * ### 開始時の値更新を監視する
   *
   * - 開始分以降の入力値をリセットする
   * - エラーメッセージをリセットする
   * - 値がリセットされた場合は処理をしない
   * - 分のセレクトボックスの選択肢を更新する
   */
  @Watch("startHour")
  public changeStartHour(newValue: string): void {
    // 更新対象の外出外泊届け、外出外泊設定が未取得の場合は処理をスキップする
    // 初期化処理が終わるまでは処理をスキップする
    if (!this.outing || !this.setting || !this.isInitialized) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      this.startMin = "";
      this.stayingCount = "";
      this.endHour = "";
      this.endMin = "";

      this.outingDateErrorMessage1 = "";
      this.outingDateErrorMessage2 = "";

      if (!newValue) {
        this.isBusy = false;
        return;
      }

      const startMins: Array<string> = this.createStartMins(Number(this.startDay), Number(newValue));
      this.startMins = startMins;

      if (startMins.length == 0) {
        this.outingDateErrorMessage1 = "選択した開始時は外出外泊届けが出来ません。他の開始時を選択してください。";
      }
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 開始分の値更新を監視する
   *
   * - 宿泊数以降の入力値をリセットする
   * - エラーメッセージをリセットする
   * - 値がリセットされた場合は処理をしない
   * - 外出の場合は終了日、外泊の場合は宿泊数のセレクトボックスの選択肢を更新する
   */
  @Watch("startMin")
  public async changeStartMin(newValue: string): Promise<void> {
    // 更新対象の外出外泊届け、外出外泊設定が未取得の場合は処理をスキップする
    // 初期化処理が終わるまでは処理をスキップする
    if (!this.outing || !this.setting || !this.isInitialized) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      this.stayingCount = "";
      this.endHour = "";
      this.endMin = "";

      this.outingDateErrorMessage1 = "";
      this.outingDateErrorMessage2 = "";

      if (!newValue) {
        this.isBusy = false;
        return;
      }

      // 外出の場合は終了日の選択肢を更新する
      if (this.outing?.type == OutingType.GOING_OUT) {
        const endHours: Array<string> = await this.createEndHours();
        this.endHours = endHours;

        if (endHours.length == 0) {
          this.outingDateErrorMessage1 = "選択した開始分は外出外泊届けが出来ません。他の開始分を選択してください。";
        }
      }
      // 外泊の場合は宿泊数の選択肢を更新する
      else if (this.outing?.type == OutingType.STAYING_OUT) {
        const startDatetime = this.$dateUtil.convertDateToNumber(
          new Date(Number(this.startYear), Number(this.startMonth) - 1, Number(this.startDay), Number(this.startHour), Number(this.startMin)),
          "yyyymmdd000000000"
        );
        const stayingCounts: Array<{ label: string; value: string }> = await this.createStayingCounts(startDatetime);
        this.stayingCounts = stayingCounts;

        if (stayingCounts.length == 0) {
          this.outingDateErrorMessage2 = "選択した開始分は外出外泊届けが出来ません。他の開始分を選択してください。";
        }
      }
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 宿泊数の値更新を監視する
   *
   * - 終了時以降の入力値をリセットする
   * - エラーメッセージをリセットする
   * - 値がリセットされた場合は処理をしない
   * - 終了時のセレクトボックスの選択肢を更新する
   */
  @Watch("stayingCount")
  public async changeStayingCounts(newValue: string): Promise<void> {
    // 更新対象の外出外泊届け、外出外泊設定が未取得の場合は処理をスキップする
    // 初期化処理が終わるまでは処理をスキップする
    if (!this.outing || !this.setting || !this.isInitialized) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      this.endHour = "";
      this.endMin = "";

      this.outingDateErrorMessage1 = "";
      this.outingDateErrorMessage2 = "";

      if (!newValue) {
        this.isBusy = false;
        return;
      }

      const endHours: Array<string> = await this.createEndHours();
      this.endHours = endHours;

      if (endHours.length == 0) {
        this.outingDateErrorMessage2 = "選択した宿泊数は外出外泊届けが出来ません。他の宿泊数を選択してください。";
      }
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 終了時の値更新を監視する
   *
   * - 終了分以降の入力値をリセットする
   * - エラーメッセージをリセットする
   * - 値がリセットされた場合は処理をしない
   * - 終了分のセレクトボックスの選択肢を更新する
   */
  @Watch("endHour")
  public changeEndHour(newValue: string): void {
    // 更新対象の外出外泊届け、外出外泊設定が未取得の場合は処理をスキップする
    // 初期化処理が終わるまでは処理をスキップする
    if (!this.outing || !this.setting || !this.isInitialized) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      this.endMin = "";

      this.outingDateErrorMessage1 = "";
      this.outingDateErrorMessage2 = "";

      if (!newValue) {
        this.isBusy = false;
        return;
      }

      const endMins: Array<string> = this.createEndMins(newValue);
      this.endMins = endMins;

      if (endMins.length == 0) {
        // 外出
        if (this.outing?.type == OutingType.GOING_OUT) {
          this.outingDateErrorMessage1 = "選択した終了時は外出外泊届けが出来ません。他の終了時を選択してください。";
        }
        // 外泊
        else if (this.outing?.type == OutingType.STAYING_OUT) {
          this.outingDateErrorMessage2 = "選択した終了時は外出外泊届けが出来ません。他の終了時を選択してください。";
        }
      }
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const setting: OutingSettingGetModel = await this.getOutingSetting();
    this.setting = setting;

    const id: number = Number(this.$route.params.id);
    const outing: OutingGetModel = await this.getOuting(id);
    this.setOuting(outing, setting);

    // 初期化が終わったことを格納する
    // Watchが動かないように、2秒待つ
    await this.$timerUtil.setTimeout(2000);
    this.isInitialized = true;

    this.isBusy = false;
    return;
  }

  /**
   * ### 外出外泊届け設定取得
   */
  public async getOutingSetting(): Promise<OutingSettingGetModel> {
    const response: OutingSettingGetModel = await this.outingSettingsService.get(userStore);
    return response;
  }

  /**
   * ### 外出外泊届けを取得する
   */
  public async getOuting(id: number): Promise<OutingGetModel> {
    const response: OutingGetModel = await this.outingsService.get(userStore, id);
    return response;
  }

  public async setOuting(outing: OutingGetModel, setting: OutingSettingGetModel): Promise<void> {
    this.outing = outing;

    // 外出または外泊の開始・終了日時を格納する
    this.startYear = this.$dateUtil.convertNumberToString(outing.startDatetime, "yyyy");

    this.startMonths = this.createStartMonths(Number(this.startYear));
    this.startMonth = this.$dateUtil.convertNumberToString(outing.startDatetime, "mm", false);

    this.startDays = await this.createStartDays(Number(this.startYear), Number(this.startMonth));
    this.startDay = this.$dateUtil.convertNumberToString(outing.startDatetime, "dd", false);

    this.startHours = this.createStartHours(Number(this.startDay));
    this.startHour = this.$dateUtil.convertNumberToString(outing.startDatetime, "hh", false);

    this.startMins = this.createStartMins(Number(this.startDay), Number(this.startHour));
    this.startMin = this.$dateUtil.convertNumberToString(outing.startDatetime, "MM", false);

    if (outing.type == OutingType.STAYING_OUT) {
      // 宿泊日数を計算する
      this.stayingCount = this.calculateStayingCount(outing);
      // 宿泊日数選択肢を計算する
      this.stayingCounts = await this.createStayingCounts(outing.startDatetime);
    }

    this.endHours = await this.createEndHours();
    this.endHour = this.$dateUtil.convertNumberToString(outing.endDatetime, "hh", false);

    this.endMins = this.createEndMins(this.endHour);
    this.endMin = this.$dateUtil.convertNumberToString(outing.endDatetime, "MM", false);

    // お迎え（自由記述）が存在しない場合は初期値を作成する
    if (!outing.pickupPerson) {
      outing.pickupPerson = {
        name: "",
        relation: "",
        tel: ""
      };
    }
    // 外出手段が存在しない場合は初期値を作成する
    if (!outing.selectedVehicle) {
      outing.selectedVehicle = "";
    }
    // 開始日の食事情報を格納する
    // 設定で使用しない食事は「しない」で上書きする
    this.startDateNeedsMeal = outing.startDateNeedsMeal;
    this.startDateNeedsMeal = this.updateDateNeedsMealBySetting(this.startDateNeedsMeal, setting);

    // 終了日の食事情報を格納する
    // 食事等が存在しない場合は初期値を作成する
    // 設定で使用しない食事は「しない」で上書きする
    if (outing.endDateNeedsMeal) {
      this.endDateNeedsMeal = outing.endDateNeedsMeal;
      this.endDateNeedsMeal = this.updateDateNeedsMealBySetting(this.endDateNeedsMeal, setting);
    } else {
      const endDateNeedsMeal = {
        breakfast: true,
        lunch: true,
        snack: true,
        dinner: true
      };
      this.endDateNeedsMeal = this.updateDateNeedsMealBySetting(endDateNeedsMeal, setting);
    }
    return;
  }

  /**
   * ### 宿泊日数を計算する
   */
  private calculateStayingCount(outing: OutingGetModel): string {
    const startDate = this.$dateUtil.convertNumberToDate(outing.startDatetime);
    const endDate = this.$dateUtil.convertNumberToDate(outing.endDatetime);

    return String(this.$dateUtil.diffDate(startDate, endDate));
  }

  /**
   * ### 開始月選択肢を作成する
   */
  public createStartMonths(year: number): Array<string> {
    const date: Date = new Date();
    const currentYear: number = date.getFullYear();
    const currentMonth: number = date.getMonth();
    const months: Array<string> = [];

    for (let i = 0; i < 12; i++) {
      if (currentYear == year && currentMonth <= i) {
        months.push(String(i + 1));
      } else if (currentYear < year) {
        months.push(String(i + 1));
      }
    }

    return months;
  }

  /**
   * ### 開始日付選択肢を作成する
   */
  public async createStartDays(year: number, month: number): Promise<Array<string>> {
    if (!this.outing) {
      return [];
    }

    const outingAvailableStartDates = await this.getAvailableStartDates(this.outing, year, month);
    const days: Array<string> = [];
    for (const availableDate in outingAvailableStartDates) {
      const slicedAvailableDate = availableDate.slice(6, 9);
      // 0埋めされているので、数値にしてから文字列に戻す
      days.push(Number(slicedAvailableDate).toString());
    }
    this.outingAvailableStartDates = outingAvailableStartDates;

    return days;
  }

  public async getAvailableStartDates(outing: OutingGetModel, year: number, month: number): Promise<OutingAvailableDatesGetModel> {
    const splitedResidentsSortKey: string[] = outing.residentsSortKey.split("_");
    const params: { type: string; residentId: number; year: number; month: number } = {
      type: this.outing?.type as OutingType,
      residentId: Number(splitedResidentsSortKey[0]),
      year: year,
      month: month
    };
    const queryParams: { exclusionid: number } = { exclusionid: outing.id };
    const response: OutingAvailableDatesGetModel = await this.outingsService.getAvailableStartDates(userStore, params, queryParams);
    return response;
  }

  /**
   * ### 開始時間選択肢を作成する
   */
  public createStartHours(day: number): Array<string> {
    if (!this.outing || !this.outingAvailableStartDates) {
      return [];
    }

    const hours: Array<string> = [];
    for (const availableDate in this.outingAvailableStartDates) {
      const slicedAvailableDate = availableDate.slice(6, 9);
      if (Number(slicedAvailableDate) == day) {
        for (const availableHour in this.outingAvailableStartDates[availableDate]) {
          hours.push(availableHour);
        }
      }
    }

    return hours;
  }

  /**
   * ### 開始分選択肢を作成する
   */
  public createStartMins(day: number, hour: number) {
    if (!this.outing || !this.outingAvailableStartDates) {
      return [];
    }

    for (const availableDate in this.outingAvailableStartDates) {
      const slicedAvailableDate = availableDate.slice(6, 9);
      if (Number(slicedAvailableDate) == day) {
        for (const availableHour in this.outingAvailableStartDates[availableDate]) {
          if (Number(availableHour) == hour) {
            return this.outingAvailableStartDates[availableDate][availableHour];
          }
        }
      }
    }

    return [];
  }

  /**
   * ### 宿泊日数選択肢を作成する
   */
  private async createStayingCounts(startDatetime: number): Promise<Array<{ label: string; value: string }>> {
    if (!this.outing) {
      return [];
    }

    const outingAvailableEndDates = await this.getAvailableEndDates(this.outing, startDatetime);
    const options: Array<{ label: string; value: string }> = [];
    const startDate = this.$dateUtil.convertNumberToDate(startDatetime);
    for (const availableDate in outingAvailableEndDates) {
      const currentDate = this.$dateUtil.convertNumberToDate(Number(`${availableDate}000000000`));
      const stayCount = this.$dateUtil.diffDate(startDate, currentDate);
      options.push({
        label: `${stayCount}泊（${this.$dateUtil.convertNumberToString(Number(`${availableDate}000000000`), "yyyy年mm月dd日")}）`,
        value: String(stayCount)
      });
    }
    this.outingAvailableEndDates = outingAvailableEndDates;

    return options;
  }

  public async getAvailableEndDates(outing: OutingGetModel, datetime: number): Promise<OutingAvailableDatesGetModel> {
    const splitedResidentsSortKey: string[] = outing.residentsSortKey.split("_");
    const params: { type: string; residentId: number; datetime: string } = {
      type: this.outing?.type as OutingType,
      residentId: Number(splitedResidentsSortKey[0]),
      datetime: this.$dateUtil.convertNumberToString(datetime, "yyyymmddhhMM")
    };
    const queryParams: { exclusionid: number } = { exclusionid: outing.id };
    const response: OutingAvailableDatesGetModel = await this.outingsService.getAvailableEndDates(userStore, params, queryParams);
    return response;
  }

  /**
   * ### 終了時間選択肢を作成する
   */
  public async createEndHours(): Promise<Array<string>> {
    if (!this.outing) {
      return [];
    }

    // 選択肢作成対象の日付を作成する
    // 更に外出の場合、終了時間選択肢の情報を取得する必要がある（外泊の場合は宿泊日数選択肢の作成時に取得しているので取得不要）
    let day = null;
    if (this.outing.type == OutingType.GOING_OUT) {
      const startDate = new Date(Number(this.startYear), Number(this.startMonth) - 1, Number(this.startDay), Number(this.startHour), Number(this.startMin));
      const startDatetime = this.$dateUtil.convertDateToNumber(startDate, "yyyymmddhhMM00000");
      const outingAvailableEndDates = await this.getAvailableEndDates(this.outing, startDatetime);
      this.outingAvailableEndDates = outingAvailableEndDates;

      day = this.$dateUtil.convertDateToString(startDate, "yyyymmdd");
    } else if (this.outing.type == OutingType.STAYING_OUT) {
      const startDate = new Date(
        Number(this.startYear),
        Number(this.startMonth) - 1,
        Number(this.startDay) + Number(this.stayingCount),
        Number(this.startHour),
        Number(this.startMin)
      );
      day = this.$dateUtil.convertDateToString(startDate, "yyyymmdd");
      await this.$timerUtil.setTimeout(1); // asyncのfunctionでawaitしないと描画が止まる問題があるので1msawaitする
    } else {
      return [];
    }

    const hours: Array<string> = [];
    for (const availableDate in this.outingAvailableEndDates) {
      if (availableDate == day) {
        for (const availableHour in this.outingAvailableEndDates[availableDate]) {
          hours.push(availableHour);
        }
      }
    }

    return hours;
  }

  /**
   * ### 終了分選択肢を作成する
   */
  public createEndMins(hour: string) {
    const startDate = new Date(
      Number(this.startYear),
      Number(this.startMonth) - 1,
      Number(this.startDay) + Number(this.stayingCount),
      Number(this.startHour),
      Number(this.startMin)
    );
    const day = this.$dateUtil.convertDateToString(startDate, "yyyymmdd");

    for (const availableDate in this.outingAvailableEndDates) {
      if (availableDate == day) {
        for (const availableHour in this.outingAvailableEndDates[availableDate]) {
          if (availableHour == hour) {
            return this.outingAvailableEndDates[availableDate][availableHour];
          }
        }
      }
    }

    return [];
  }

  /** ### 設定の「食事の使用有無」で「使用しない」ものは強制的に食事しないに更新する */
  updateDateNeedsMealBySetting(needsMeal: NeedsMeal, setting: OutingSettingGetModel): NeedsMeal {
    if (setting.useNeedsMeal.breakfast == false) {
      needsMeal.breakfast = false;
    }
    if (setting.useNeedsMeal.lunch == false) {
      needsMeal.lunch = false;
    }
    if (setting.useNeedsMeal.snack == false) {
      needsMeal.snack = false;
    }
    if (setting.useNeedsMeal.dinner == false) {
      needsMeal.dinner = false;
    }
    return needsMeal;
  }

  /**
   * ### 外出外泊届け更新確認モーダルを表示する
   */
  public async onUpdate(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      const typeName: string = OutingType.GOING_OUT ? "外出" : this.outing?.type == OutingType.STAYING_OUT ? "外泊" : "";
      this.updateConfirmModalTitle = `${typeName}届けを更新しますか？`;
      this.showUpdateConfirmModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ## 外出外泊届けを更新する
   */
  public async updateOuting(outing: OutingGetModel): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const id: number = Number(this.$route.params.id);
      const requestBody: OutingPatchModel = await this.createRequestBody(outing);
      await this.outingsService.patch(userStore, id, requestBody);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 外出外泊届け更新パラメータ作成
   */
  public createRequestBody(outing: OutingGetModel): OutingPatchModel {
    const requestBody: OutingPatchModel = {
      /** 外出、外泊種別 */
      type: outing.type,
      /** 外出外泊開始日時 */
      startDatetime: this.createRequestStartDatetime(),
      /** 外出外泊終了日時 */
      endDatetime: this.createRequestEndDatetime(),
      /** 主な外出場所（100文字） */
      location: outing.location,
      /** お迎え。申請者本人（true）、申請者以外（false） */
      applicantPickup: outing.applicantPickup,
      /** お迎え（自由記述） */
      pickupPerson: outing.applicantPickup
        ? null
        : {
            name: outing.pickupPerson?.name ? outing.pickupPerson?.name : "",
            relation: outing.pickupPerson?.relation ? outing.pickupPerson?.relation : "",
            tel: outing.pickupPerson?.tel ? outing.pickupPerson?.tel : ""
          },
      /** 外出手段 */
      selectedVehicle: outing.selectedVehicle ? outing.selectedVehicle : "",
      /** 外出外泊開始日の食事が必要かどうか */
      startDateNeedsMeal: outing.startDateNeedsMeal,
      /** 外出外泊終了日の食事が必要かどうか */
      endDateNeedsMeal: outing.type == OutingType.STAYING_OUT ? outing.endDateNeedsMeal : null,
      /** 連絡事項（1000文字） */
      text: outing.text ? outing.text : "",

      /** レコード更新日 */
      updated: outing.updated
    };

    return requestBody;
  }

  /**
   * ### リクエスト用の開始日時を作成する
   */
  public createRequestStartDatetime(): number {
    const date = new Date(Number(this.startYear), Number(this.startMonth) - 1, Number(this.startDay), Number(this.startHour), Number(this.startMin));
    return this.$dateUtil.convertDateToNumber(date, "yyyymmddhhMM00000");
  }

  /**
   * ### リクエスト用の終了日時を作成する
   */
  public createRequestEndDatetime(): number {
    // 外出
    if (this.outing?.type == OutingType.GOING_OUT) {
      const date = new Date(Number(this.startYear), Number(this.startMonth) - 1, Number(this.startDay), Number(this.endHour), Number(this.endMin));
      return this.$dateUtil.convertDateToNumber(date, "yyyymmddhhMM00000");
    }
    // 外泊
    else if (this.outing?.type == OutingType.STAYING_OUT) {
      const date = new Date(Number(this.startYear), Number(this.startMonth) - 1, Number(this.startDay), Number(this.endHour), Number(this.endMin));
      date.setDate(date.getDate() + Number(this.stayingCount));
      return this.$dateUtil.convertDateToNumber(date, "yyyymmddhhMM00000");
    }
    return 0;
  }

  /**
   * ### 外出外泊届け詳細画面へ遷移する
   */
  public gotoOutingDetail(): void {
    const id: number = Number(this.$route.params.id);
    this.$router.push({ name: PcUrl.OutingDetail, params: { id: `${id}` } });
    return;
  }
}
</script>

<style lang="scss" scoped>
#outing-edit {
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;

  .edit-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
